import {
  AIChatComplexContext,
  AIChatSearchApartmentsArgs,
} from 'src/typings/ai-chat';
import { AIChatProtocolError } from './ai-chat-protocol-error';

const MAX_PROGRESS_TEXT_LENGTH = 240;
const MAX_FINAL_TEXT_LENGTH = 4000;
const MAX_RECOMMENDED_APARTMENT_IDS = 20;
export const AI_CHAT_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;
export const AI_CHAT_TRANSCRIPTION_TIMEOUT_MS = 2 * 60 * 1000;

export { AIChatProtocolError };

const AI_CHAT_API_BASE_URL = (
  process.env.REACT_APP_AI_CHAT_API_URL || 'https://ai-chat-api.flat.services'
).replace(/\/+$/, '');

const AI_CHAT_ENDPOINT = `${AI_CHAT_API_BASE_URL}/api/v1/chat`;
const AI_CHAT_CONVERSATIONS_ENDPOINT = `${AI_CHAT_API_BASE_URL}/api/v1/conversations`;
const AI_CHAT_TRANSCRIPTIONS_ENDPOINT = `${AI_CHAT_API_BASE_URL}/api/v1/transcriptions`;

const numericRangeSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    min: { type: 'number' },
    max: { type: 'number' },
  },
};

export const AI_CHAT_APARTMENT_SEARCH_TOOL = {
  type: 'function',
  function: {
    name: 'search_apartments',
    description: [
      'Search all currently available placements of every type in the current complex.',
      'Use only criteria explicitly requested by the user or directly required to answer the request.',
      'Omit every unspecified property; never invent default ranges for rooms, bedrooms, bathrooms, area, price, or floor.',
      'For a request that only names a floor, filter only by that floor and leave all other criteria unset.',
      'Do not use a catalog field as a filter when its value is unknown or null unless the user explicitly asks for that field.',
      'Keep numeric ranges in the merged catalog units and never guess their bounds.',
      'Interpret totalPrice directly in the merged catalog primary price units.',
      'Do not add a currency field to the search arguments.',
      'Return a broader useful set and choose final recommendations only from its IDs.',
    ].join(' '),
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        rooms: numericRangeSchema,
        bedrooms: numericRangeSchema,
        bathrooms: numericRangeSchema,
        totalArea: numericRangeSchema,
        totalPrice: numericRangeSchema,
        floor: numericRangeSchema,
        excludedFloors: { type: 'array', items: { type: 'number' }, maxItems: 100 },
        houses: { type: 'array', items: { type: 'string' }, maxItems: 100 },
        sections: { type: 'array', items: { type: 'string' }, maxItems: 100 },
        planningTypes: { type: 'array', items: { type: 'string' }, maxItems: 100 },
        placementTypes: { type: 'array', items: { type: 'string' }, maxItems: 100 },
        features: { type: 'array', items: { type: 'string' }, maxItems: 100 },
        sort: {
          type: 'object',
          additionalProperties: false,
          required: ['field', 'direction'],
          properties: {
            field: { type: 'string', enum: ['totalPrice', 'totalArea', 'floor'] },
            direction: { type: 'string', enum: ['asc', 'desc'] },
          },
        },
        limit: { type: 'integer', minimum: 1, maximum: 20 },
      },
    },
  },
};

export interface AIChatMessageRequest {
  text: string;
  conversationId: string | null;
  context: AIChatComplexContext;
}

export interface AIChatResponseOptions {
  onConversationId?: (conversationId: string) => void;
  onResponse?: (response: AIChatWireResponse) => void;
}

export interface AIChatCompletedResult {
  status: 'completed';
  conversationId: string;
  text: string;
  recommendedApartmentIds: Array<string | number>;
}

export interface AIChatToolCall {
  toolCallId: string;
  name: string;
  arguments: AIChatSearchApartmentsArgs;
}

export interface AIChatToolCallsResult {
  status: 'tool_calls';
  conversationId: string;
  calls: AIChatToolCall[];
  text?: string;
}

export type AIChatWireResponse = AIChatCompletedResult | AIChatToolCallsResult;

interface AIChatToolResultRequestBase {
  conversationId: string;
  toolCallId: string;
}

export type AIChatToolResultRequest = AIChatToolResultRequestBase & (
  | { status: 'success'; data: unknown; apartmentIds: Array<string | number> }
  | { status: 'error'; error: string }
);

export class AIChatApiError extends Error {
  status: number;

  code?: string;

  conversationId?: string;

  constructor(status: number, message: string, code?: string, conversationId?: string) {
    super(message);
    this.name = 'AIChatApiError';
    this.status = status;
    this.code = code;
    this.conversationId = conversationId;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const parseJson = async (response: Response, invalidMessage: string): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    throw new Error(invalidMessage);
  }
};

const getServerErrorMessage = (body: unknown, status: number): string => {
  if (isRecord(body)) {
    if (typeof body.message === 'string' && body.message.length > 0) return body.message;
    if (typeof body.error === 'string' && body.error.length > 0) return body.error;
  }
  return `AI chat request failed with HTTP ${status}`;
};

const throwHttpError = async (
  response: Response,
  onConversationId?: (conversationId: string) => void,
): Promise<never> => {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  const code = isRecord(body) && typeof body.code === 'string' ? body.code : undefined;
  const conversationId = isRecord(body) && typeof body.conversationId === 'string'
    && body.conversationId.length > 0 ? body.conversationId : undefined;
  if (conversationId) onConversationId?.(conversationId);
  throw new AIChatApiError(
    response.status,
    getServerErrorMessage(body, response.status),
    code,
    conversationId,
  );
};

const hasConversationId = (value: Record<string, unknown>) => (
  typeof value.conversationId === 'string' && value.conversationId.length > 0
);

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const isNumericRange = (value: unknown): boolean => (
  value === undefined
  || (isRecord(value)
    && Object.keys(value).every((key) => key === 'min' || key === 'max')
    && (value.min === undefined || isFiniteNumber(value.min))
    && (value.max === undefined || isFiniteNumber(value.max)))
);

const isBoundedNumberArray = (value: unknown): boolean => (
  value === undefined
  || (Array.isArray(value) && value.length <= 100 && value.every(isFiniteNumber))
);

const isBoundedStringArray = (value: unknown): boolean => (
  value === undefined
  || (Array.isArray(value) && value.length <= 100 && value.every((item) => typeof item === 'string'))
);

const isSearchArguments = (candidate: unknown): candidate is AIChatSearchApartmentsArgs => {
  if (!isRecord(candidate)) return false;
  const value = candidate;
  const allowedKeys = new Set([
    'rooms', 'bedrooms', 'bathrooms', 'totalArea', 'totalPrice', 'floor',
    'excludedFloors', 'houses', 'sections', 'planningTypes', 'placementTypes', 'features', 'sort', 'limit',
  ]);
  if (Object.keys(value).some((key) => !allowedKeys.has(key))) return false;
  if (!['rooms', 'bedrooms', 'bathrooms', 'totalArea', 'totalPrice', 'floor']
    .every((key) => isNumericRange(value[key]))) return false;
  if (!isBoundedNumberArray(value.excludedFloors) || !isBoundedStringArray(value.houses)
    || !isBoundedStringArray(value.sections) || !isBoundedStringArray(value.planningTypes)
    || !isBoundedStringArray(value.placementTypes)
    || !isBoundedStringArray(value.features)) return false;
  if (value.sort !== undefined && (!isRecord(value.sort)
    || Object.keys(value.sort).some((key) => key !== 'field' && key !== 'direction')
    || !['totalPrice', 'totalArea', 'floor'].includes(String(value.sort.field))
    || !['asc', 'desc'].includes(String(value.sort.direction)))) return false;
  return value.limit === undefined
    || (typeof value.limit === 'number' && Number.isInteger(value.limit) && value.limit >= 1 && value.limit <= 20);
};

const isCompletedResponse = (value: unknown): value is AIChatCompletedResult => {
  if (!isRecord(value) || value.status !== 'completed' || !hasConversationId(value)) return false;
  if (typeof value.text !== 'string' || value.text.length < 1 || value.text.length > MAX_FINAL_TEXT_LENGTH) return false;
  return Array.isArray(value.recommendedApartmentIds)
    && value.recommendedApartmentIds.length <= MAX_RECOMMENDED_APARTMENT_IDS
    && value.recommendedApartmentIds.every((id) => (
      typeof id === 'string' || (typeof id === 'number' && Number.isInteger(id))
    ));
};

const isToolCallsResponse = (value: unknown): value is AIChatToolCallsResult => {
  if (!isRecord(value) || value.status !== 'tool_calls' || !hasConversationId(value)) return false;
  if (!Array.isArray(value.calls) || value.calls.length !== 1) return false;
  if (value.text !== undefined && (typeof value.text !== 'string' || value.text.length > MAX_PROGRESS_TEXT_LENGTH)) return false;
  return value.calls.every((call) => (
    isRecord(call)
    && typeof call.toolCallId === 'string'
    && call.toolCallId.length > 0
    && typeof call.name === 'string'
    && call.name === AI_CHAT_APARTMENT_SEARCH_TOOL.function.name
    && isRecord(call.arguments)
    && isSearchArguments(call.arguments)
  ));
};

const postChat = async (
  body: Record<string, unknown>,
  options?: AIChatResponseOptions,
): Promise<AIChatWireResponse> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_CHAT_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(AI_CHAT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) return throwHttpError(response, options?.onConversationId);

    const result = await parseJson(response, 'Invalid JSON returned by AI chat API');
    const conversationId = isRecord(result) && hasConversationId(result)
      ? result.conversationId as string
      : undefined;
    if (conversationId) options?.onConversationId?.(conversationId);
    if (!isCompletedResponse(result) && !isToolCallsResponse(result)) {
      throw new AIChatProtocolError('Invalid AI chat response', conversationId);
    }
    options?.onResponse?.(result);
    return result;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const sendAIApartmentChatMessage = (
  request: AIChatMessageRequest,
  options?: AIChatResponseOptions,
): Promise<AIChatWireResponse> => postChat(request.conversationId
  ? { action: 'message', conversationId: request.conversationId, text: request.text }
  : {
    action: 'message',
    text: request.text,
    context: request.context,
    tools: [AI_CHAT_APARTMENT_SEARCH_TOOL],
  }, options);

export const sendAIApartmentChatToolResult = (
  request: AIChatToolResultRequest,
  options?: AIChatResponseOptions,
): Promise<AIChatWireResponse> => {
  const result = request.status === 'success'
    ? {
      toolCallId: request.toolCallId,
      status: request.status,
      data: request.data,
      apartmentIds: request.apartmentIds,
    }
    : { toolCallId: request.toolCallId, status: request.status, error: request.error };
  return postChat({
    action: 'tool_results',
    conversationId: request.conversationId,
    results: [result],
  }, options);
};

export async function deleteAIChatConversation(conversationId: string): Promise<void> {
  const response = await fetch(
    `${AI_CHAT_CONVERSATIONS_ENDPOINT}/${encodeURIComponent(conversationId)}`,
    { method: 'DELETE' },
  );

  if (response.status === 204 || response.status === 404) return;
  if (!response.ok) await throwHttpError(response);
}

export async function transcribeAIChatAudio(audio: Blob): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_CHAT_TRANSCRIPTION_TIMEOUT_MS);

  try {
    const response = await fetch(AI_CHAT_TRANSCRIPTIONS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': audio.type || 'audio/webm' },
      body: audio,
      signal: controller.signal,
    });
    if (!response.ok) return throwHttpError(response);

    const result = await parseJson(response, 'Invalid JSON returned by AI chat API');
    if (!isRecord(result) || typeof result.text !== 'string') {
      throw new Error('Invalid transcription returned by AI chat API');
    }
    const text = result.text.trim();
    if (!text) throw new Error('No speech was detected');
    return text;
  } finally {
    clearTimeout(timeoutId);
  }
}
